import { AppLayout } from "@bcc/components/app-layout";
import { getLocalVariantsWithProductData } from "@bcc/components/products/products-table/products-table.utils";
import { ProductsView } from "@bcc/components/views/products-view";
import { useRBAC } from "@bcc/contexts/rbac-context";
import { useInstallationsState } from "@client-shared/providers/installations";
import { useProductsState } from "@client-shared/providers/products";
import { useVendorContext } from "@client-shared/providers/vendor";
import { getServiceProducts } from "@client-shared/services/api/product";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import {
  buildPimFirstFormProducts,
  filterRejectedSalesforceProducts,
  mergeSalesforceAndPimProducts,
  withSalesforceIntensity,
  productToFormProduct,
} from "./products-page.utils";

const SS_PRODUCTS_QUERY_KEY = "ss-products" as const;

/**
 * SS Roaster products page — fetches directly from PIM + Salesforce
 * instead of the installation-based flow.
 */
function SsProductsPageContent() {
  const [{ currentVendor }] = useVendorContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [SS_PRODUCTS_QUERY_KEY, currentVendor?.userId, currentVendor?.region],
    queryFn: async () => {
      const [pim, salesforce] = await Promise.all([
        getServiceProducts(currentVendor!, "pim"),
        getServiceProducts(currentVendor!, "salesforce"),
      ]);
      return { pim, salesforce };
    },
    enabled: Boolean(currentVendor),
  });

  // Unfiltered merge — drives duplicate-title validation in
  // SsSkuOnboardingModal. Hidden Draft/Rejected SKUs must still block
  // creating another SKU with the same name, so this list must include them.
  const mergedProducts = React.useMemo(() => mergeSalesforceAndPimProducts(data?.salesforce ?? [], data?.pim ?? []), [data]);

  // PIM-only — powers the table rows and the "New Bag Size" search modal. A
  // bag size can only be added to something that's actually live in PIM, and
  // in-progress/Rejected Salesforce records now surface via their own
  // dedicated entry points (Rejected Products list) instead of as table rows.
  const pimFormProducts = React.useMemo(
    () => withSalesforceIntensity((data?.pim ?? []).map(productToFormProduct), data?.salesforce ?? []),
    [data]
  );

  // Every Salesforce record, whatever its approval status — the provisional
  // intensity markers mean "scored but not yet approved", which includes Draft
  // and Submitted, not just Rejected.
  const salesforceFormProducts = React.useMemo(() => (data?.salesforce ?? []).map(productToFormProduct), [data]);

  const variantData = React.useMemo(() => getLocalVariantsWithProductData(pimFormProducts), [pimFormProducts]);

  // Rejected Salesforce records (pre-PIM, no webStatus yet) — feeds the
  // "Rejected Products" modal opened from the SKU selection popup.
  const rejectedProducts = React.useMemo(() => filterRejectedSalesforceProducts(mergedProducts), [mergedProducts]);

  const existingProductTitles = React.useMemo(
    () => mergedProducts.map((product) => product.title).filter((title): title is string => Boolean(title)),
    [mergedProducts]
  );

  return (
    <AppLayout>
      <ProductsView
        variantData={variantData}
        isLoading={isLoading}
        error={error ?? undefined}
        handleReload={refetch}
        existingProducts={pimFormProducts}
        existingProductTitles={existingProductTitles}
        rejectedProducts={rejectedProducts}
        salesforceProducts={salesforceFormProducts}
      />
    </AppLayout>
  );
}

function StandardProductsPageContent() {
  const [installationsState, , installationsMethods] = useInstallationsState();
  const [productsState, , productsMethods] = useProductsState();

  const installedIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (
      !installationsState.initialized ||
      (installationsState.installations && installationsState.installations.size > 0) ||
      installationsState.error ||
      !installationsMethods?.fetch
    ) {
      return;
    }
    installationsMethods.fetch();
  }, [installationsState.initialized, installationsState.installations, installationsState.error, installationsMethods]);

  React.useEffect(() => {
    if (!installationsState.installations || installationsState.installations.size === 0 || !productsMethods?.handleInstallationId) {
      return;
    }
    const installationId = installationsState.installations.keys().next().value;
    if (!installationId) return;
    if (installedIdRef.current === installationId && productsState.installationId === installationId) {
      return;
    }
    installedIdRef.current = installationId;
    productsMethods.handleInstallationId(installationId);
  }, [installationsState.installations, productsMethods, productsState.installationId]);

  // `remoteProducts` now only feeds the live-inventory overlay on surviving
  // PIM variants, so a late/failed remote fetch degrades gracefully (inventory
  // numbers fill in on a later render) instead of blocking the whole table.
  // Keep the existing table visible during connection refreshes, such as the
  // post-disconnect fetch. Initial load still blocks until connections arrive.
  const isConnectionsInitialLoad = productsState.loading.connections && productsState.connections === undefined;
  const isLoading = productsState.loading.localProducts || isConnectionsInitialLoad || productsState.loading.serviceProducts;

  // Extract serviceId from the selected installation (matches productsState.installationId)
  const activeServiceId = React.useMemo(() => {
    const installations = installationsState.installations;
    const installationId = productsState.installationId;
    if (!installations || !installationId) return undefined;
    return installations.get(installationId)?.serviceId;
  }, [installationsState.installations, productsState.installationId]);

  // PIM-first data source. `localParentChildProducts` is the grouped PIM
  // catalog; `buildPimFirstFormProducts` keeps the full catalog visible, then
  // overlays Salesforce pricing/status and live installation inventory for
  // connected variants via the connection targetVariantId. PIM `webStatus`
  // always wins, so `filterOutHiddenSalesforceStatuses` would be a no-op here.
  // ShipStation keeps it for its Salesforce-first pipeline.
  const formProducts = React.useMemo(
    () =>
      buildPimFirstFormProducts(productsState.localParentChildProducts ?? [], {
        salesforceProducts: productsState.serviceProducts ?? [],
        connections: productsState.connections ?? [],
        remoteProducts: productsState.remoteProducts ?? [],
      }),
    [productsState.localParentChildProducts, productsState.serviceProducts, productsState.connections, productsState.remoteProducts]
  );

  const variantData = React.useMemo(() => getLocalVariantsWithProductData(formProducts, activeServiceId), [formProducts, activeServiceId]);

  const existingProductTitles = React.useMemo(
    () =>
      formProducts
        .filter((product) => !product.variants?.some((v) => v.isImportedLocalVariant))
        .map((product) => product.title)
        .filter((title): title is string => Boolean(title)),
    [formProducts]
  );

  const handleReload = React.useCallback(() => {
    if (productsState.installationId && productsMethods?.handleInstallationId) {
      productsMethods.handleInstallationId(productsState.installationId);
    }
  }, [productsState.installationId, productsMethods]);

  return (
    <AppLayout>
      <ProductsView
        variantData={variantData}
        isLoading={isLoading}
        handleReload={handleReload}
        allowPimVariantIdAsSkuCode
        error={productsState.error.localProducts ?? productsState.error.serviceProducts ?? productsState.error.connections}
        existingProductTitles={existingProductTitles}
      />
    </AppLayout>
  );
}

export function ProductsPage() {
  const { featureName } = useRBAC();
  const isShipstationRoaster = featureName === "SS";

  if (isShipstationRoaster) {
    return <SsProductsPageContent />;
  }

  return <StandardProductsPageContent />;
}
